/**
 * DirectoryMapView: the resource directory's map, the second home of the
 * capacity map's LeafletMap (one map component, two homes).
 *
 * Two pin sets ride the map's own layer, both in the tenant's provider
 * colour (useMapColors, Admin > Units > Map colours) and both narrowed
 * to whatever the directory's lists are showing, because the caller
 * hands in the SAME filtered rows the lists render (useUnifiedSearch's
 * orgResults and contactResults), so switching between list and map
 * never changes who is in play:
 *
 *   - organizations: one filled pin per active organization with a
 *     geocoded address (GET /providers/map pins). Its card lists the
 *     people we know there: every active person affiliated with it,
 *     each linking to the person's page. An affiliated person's place
 *     IS their organization's, so they never get a pin of their own and
 *     the people filters never thin an organization's people.
 *   - people without an organization: one pin in the people colour per person
 *     with no active organization link and a geocoded address of their
 *     own (individual_pins). The ring is the same colour, a different
 *     mark: a person, not a place you can send someone.
 *
 * What the map cannot draw is listed under it, organizations and people
 * apart, one group per reason: no address on file; an address looked up
 * and not found; one not found with no state typed, whose line names the
 * tenant's working states and asks for the state; text with no street
 * address; and an address on file not looked up yet (the backfill, or
 * the next edit, places it).
 *
 * The map opens on the tenant's working area: the box getTenantConfig
 * carries (computed by the server from staff home points), the whole
 * country when that read fails. It needs no client read: the map mounts
 * once the config has settled, fits that first box once and keeps it, so
 * a refetched config never refits it. After that it moves only when the
 * person asks (LeafletMap's focus request): once to the client the URL
 * names when the map opens, and once per client search choice, only to a
 * point inside the working area. A hover, a tap, typing, a layer, a new
 * list or a refetch never moves it. Every pin is still drawn and listed,
 * however far outside the box.
 *
 * Hovering (desktop) or tapping a pin shows a card beside it. An
 * organization's card: name, category, phone, the Recommended badge, the
 * placement precision when it is not the street, its people, and a link
 * to the organization's page. A person's card: name, title, specialties,
 * phone, the Recommended badge, the precision line, and a link to the
 * person's page. A tapped card stays until the pin is tapped again or
 * the card is closed, so its links can be clicked; a hovered card
 * follows the mouse off. A tapped card closes from its Close button, a
 * tap or click anywhere on the map outside it (a pin aside, which opens
 * its own card), or Escape. A touch screen reports a tap as a hover too
 * and never as the hover ending, so closing clears both. Closing redraws
 * the pins, and the browser then reports the pin under a resting pointer
 * as hovered again although nothing moved; that report is ignored until
 * the pointer is somewhere else, so a closed card stays closed.
 *
 * The bar. Everything that steers the map sits
 * in one sticky bar under the app header, two lines that never wrap: the
 * client search and the three layer pills (each with its colour as its
 * legend and the count it draws), the pills scrolling sideways when the
 * line is short; then the Directory's own filter row, exactly as the list
 * shows it (filterRow), on its own line at every width. Below 760px the
 * search and the pills take a line each, so neither is squeezed, and the
 * map stands a little further in from the screen's edges (PHONE_MAP_INSET)
 * so a thumb can scroll the page beside it. The map fills the
 * window under the bar, and what it has to say (the picked client, the
 * outside line, an empty result, the layers being off) sits on the map's
 * corner, so nothing pushes it down. Which layers open on is the rule in
 * directoryMapLayers.ts; a hand toggle holds until the Directory is left.
 *
 * Clients appear two ways, both only for a viewer who may read client
 * records (the Clients layer's own floor):
 *   - "My clients", on at open for a seat with a caseload, draws the
 *     viewer's own client pins from GET /map/clients: the pins the server
 *     marks `mine` (on her caseload by any arm, _caseload.ts), not every
 *     client she may open;
 *   - the map's client search (MapClientSearch, first in the toolbar)
 *     finds ONE client by name in that same read, which it starts the
 *     first time the field takes focus, never at mount. The choice is the
 *     client the cards measure from: their pin sits at the client's
 *     current location (LeafletMap's placementPin, the darkest fill on
 *     the map), and every organization and person card also says how far
 *     it is from them (haversineMiles, straight-line). The map moves to
 *     the client only when they are inside the working area; otherwise
 *     the pin is drawn, the map stays, and a line on the map says so. The chosen client lives in the directory URL (?near=<clientId>)
 *     so a client's record can open the map already centred on them.
 *     Never a gate: the map is complete without a client chosen. A client
 *     who is not on the map is named with the reason
 *     (clientNotPlacedLabel) in the chip (MapClientChip), and a client
 *     placed only by town or ZIP is marked approximate on the pin and
 *     the chip.
 *
 * An organization or person pin placed approximately (precision 'zip') is
 * marked on the pin's title and on its card, in one sentence.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { capitalizeFirst } from '@/lib/capitalizeFirst';
import { Button } from '@/components/primitives/Button';
import { FilterPill } from '@/components/primitives/FilterPill';
import { PAGE_GUTTER, PAGE_WIDTH_CLASSES } from '@/components/primitives/PageShell';
import { useIsMobile } from '@/hooks/useIsMobile';
import { RecommendedBadge } from '@/components/RecommendedBadge';
import { tileUrlTemplate, useClientMap, useMapColors, type ClientMapWire } from '@/lib/capacityApi';
import {
  useProviderMap,
  type IndividualMapNotPlacedWire,
  type IndividualMapPinWire,
  type ProviderMapNotPlacedReason,
  type ProviderMapNotPlacedWire,
  type ProviderMapPinWire,
} from '@/lib/directoryMapApi';
import { haversineMiles, type LatLng } from '@/lib/geo';
import { APPROXIMATE_LOCATION_TEXT, APPROXIMATE_SUFFIX, clientNotPlacedLabel, notPlacedLabel, type ClientNotPlacedReason } from '@/lib/clientLocation';
import { insideBounds, readWorkingArea, type MapBounds } from '@/lib/mapView';
import { readWorkingStates, type WorkingState } from '@/lib/tenantConfig';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { formatPhoneDisplay } from '@/lib/phone';
import { categoryDisplayFromSlug } from '@/lib/uiCategoryLabels';
import type { IndividualForSearch, ProviderForSearch } from '@/lib/types';
import {
  CM_PIN_RADIUS,
  HOVER_RADIUS_DELTA,
  HOVER_WEIGHT,
  LeafletMap,
  useFocusRequest,
  type MapPin,
} from '@/components/capacity/LeafletMap';
import { MapClientChip, type MapClientChipState } from '@/components/capacity/MapClientChip';
import { MapClientSearch } from '@/components/capacity/MapClientSearch';
import { PLACEMENT_OUTSIDE_AREA_TEXT } from '@/components/capacity/PlaceClientPanel';
import { NotPlacedGroup } from '@/components/capacity/NotPlacedGroup';
import { useTenant } from '@/lib/auth';
import { canViewDirectoryGaps } from '@/lib/capabilities';
import {
  DIRECTORY_LAYERS_OFF_TEXT,
  directoryMapLayerDefaults,
  resolveDirectoryMapLayers,
  type DirectoryMapLayerKey,
  type DirectoryMapLayerOverrides,
} from '@/lib/directoryMapLayers';

export type DirectoryMapViewMode = 'all' | 'organizations' | 'people';

export interface DirectoryMapViewProps {
  tenantSlug: string;
  /** The directory's filtered organizations — the rows the list shows
   *  (empty in the people view). */
  organizations: ProviderForSearch[];
  /** The directory's filtered people — the rows the people list shows
   *  (empty in the organizations view). */
  individuals: IndividualForSearch[];
  /** Which lists the directory is showing: decides the wording of the
   *  count and the empty state; the rows decide what draws. */
  view: DirectoryMapViewMode;
  /** read_clinical_record: whether the two client affordances render. */
  canShowClients: boolean;
  /** The client the search chose, from the directory URL. */
  nearClientId: string | null;
  onNearClientChange: (clientId: string | null) => void;
  /** A Directory filter is set: an input to the layer rule. */
  filtersActive?: boolean;
  /** The Directory search box holds text: an input to the layer rule. */
  searchActive?: boolean;
  /** The layers switched by hand earlier on this visit to the Directory
   *  (the view unmounts on List; the page keeps them). */
  initialLayerOverrides?: DirectoryMapLayerOverrides;
  onLayerOverridesChange?: (next: DirectoryMapLayerOverrides) => void;
  /** The Directory's filter row, the bar's second line. */
  filterRow?: ReactNode;
}

/** Which pin a card is anchored to. Organization and person ids are
 *  distinct uuids, but the map's one pin layer needs one id space, so a
 *  person's marker id carries a prefix. */
interface CardAnchor {
  /** LP-43: `client` is the Near client's own pin. */
  kind: 'organization' | 'person' | 'client';
  id: string;
  point: { x: number; y: number };
}

const PERSON_MARKER_PREFIX = 'person:';

function markerIdFor(kind: CardAnchor['kind'], id: string): string {
  return kind === 'person' ? `${PERSON_MARKER_PREFIX}${id}` : id;
}

function anchorFromMarker(markerId: string, point: { x: number; y: number }): CardAnchor {
  return markerId.startsWith(PERSON_MARKER_PREFIX)
    ? { kind: 'person', id: markerId.slice(PERSON_MARKER_PREFIX.length), point }
    : { kind: 'organization', id: markerId, point };
}

/** The chosen client's placement on the map, or why there is none. */
type NearClientState =
  | { kind: 'placed'; id: string; name: string; point: LatLng; approximate: boolean }
  | { kind: 'not_placed'; id: string; name: string; reason: ClientNotPlacedReason }
  | { kind: 'unknown'; id: string }
  | { kind: 'loading'; id: string }
  | { kind: 'none' };

/** The not-placed groups, in this order: nothing to place, then the
 *  addresses looked up and not placed, then the ones not looked up yet. */
const NOT_PLACED_GROUP_ORDER: readonly ProviderMapNotPlacedReason[] = [
  'no_address',
  'not_found',
  'not_found_add_state',
  'no_street_address',
  'not_checked',
];

/** A reason's words: no address on file, or the placement rule's line. */
function notPlacedGroupLabel(reason: ProviderMapNotPlacedReason, workingStates: readonly WorkingState[]): string {
  return reason === 'no_address' ? 'No address on file' : notPlacedLabel(reason, workingStates);
}

export const PROVIDER_PIN_RADIUS = CM_PIN_RADIUS;

const NO_PINS: ClientMapWire['pins'] = [];
const NO_NOT_PLACED: ClientMapWire['not_placed'] = [];
/** Client pins draw a step under the provider pins, the capacity map's client size. */
const CLIENT_PIN_RADIUS = 6.5;

/** The app header's height (AppShell: min-h 68px desktop, 60px phone, plus its hairline). */
const HEADER_PX = 69;
const HEADER_PX_PHONE = 61;
/** The phone's fixed bottom tab bar, which the map stops above. */
const TAB_BAR_PX_PHONE = 80;
/** The room above and below the map inside the window. */
const MAP_GAP_PX = 16;
/** The map never shrinks below this, so a short window still has a map. */
const MAP_MIN_HEIGHT = '20rem';
/** Below 760px the map stands this much further in than the page gutter
 *  (PAGE_GUTTER, 16px) on each side: 8px more, 24px from the screen's
 *  edge, room for a thumb to scroll the page beside the map. */
const PHONE_MAP_INSET = 'mx-2';
/** A redrawn pin's hover within this many pixels of where the pointer was
 *  when a card closed is the browser re-reporting a resting pointer. */
const RESTING_POINTER_PX = 2;
/** A first guess at the bar's height until it is measured. */
const BAR_PX_GUESS = 120;

/** The bar's height, measured as it changes (root text size, a phone turned). */
function useMeasuredHeight(ref: RefObject<HTMLElement | null>, fallback: number): number {
  const [height, setHeight] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setHeight(el.getBoundingClientRect().height));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return height;
}

export function formatDistanceMiles(miles: number): string {
  return `${miles.toFixed(1)} mi`;
}

export function DirectoryMapView({
  tenantSlug,
  organizations,
  individuals,
  view,
  canShowClients,
  nearClientId,
  onNearClientChange,
  filtersActive = false,
  searchActive = false,
  initialLayerOverrides,
  onLayerOverridesChange,
  filterRow,
}: DirectoryMapViewProps): ReactNode {
  const navigate = useNavigate();
  const { whoami } = useTenant();
  const isMobile = useIsMobile();
  // The two Not placed lists are for the people who fix directory addresses
  // (view_directory_gaps); a clinical view goes without them.
  const seesGaps = canViewDirectoryGaps(whoami);
  // Which layers draw: the rule (directoryMapLayers.ts) unless switched by hand.
  const [layerOverrides, setLayerOverrides] = useState<DirectoryMapLayerOverrides>(initialLayerOverrides ?? {});
  const layers = resolveDirectoryMapLayers(
    directoryMapLayerDefaults({
      seatKnown: whoami !== null,
      canShowClients,
      hasCaseload: whoami?.has_caseload === true,
      filtersActive,
      searchActive,
      nearActive: nearClientId !== null,
    }),
    layerOverrides,
  );
  const toggleLayer = (key: DirectoryMapLayerKey): void => {
    const next = { ...layerOverrides, [key]: !layers[key] };
    setLayerOverrides(next);
    onLayerOverridesChange?.(next);
  };
  const showClients = layers.clients;
  // True from the client search's first focus on.
  const [searchEngaged, setSearchEngaged] = useState(false);
  const [hovered, setHovered] = useState<CardAnchor | null>(null);
  const [pinned, setPinned] = useState<CardAnchor | null>(null);

  const colors = useMapColors(tenantSlug);
  const providerMap = useProviderMap(tenantSlug);
  // The tenant's working states, named by the add-state line.
  const tenantConfig = useTenantConfig(tenantSlug);
  const workingStates = useMemo(() => readWorkingStates(tenantConfig.data), [tenantConfig.data]);
  // The working area the map opens on: read once the config has settled
  // (the whole country when it failed), then kept for the view's life.
  const workingAreaRef = useRef<MapBounds | null>(null);
  if (workingAreaRef.current === null && !tenantConfig.isPending) {
    workingAreaRef.current = readWorkingArea(tenantConfig.data);
  }
  const workingArea = workingAreaRef.current;
  // The client read is fetched only when something on the map needs it:
  // the Clients layer, the client search once focused, or a client already
  // named in the URL.
  const wantsClients = canShowClients && (showClients || searchEngaged || nearClientId !== null);
  const clientMap = useClientMap(tenantSlug, { enabled: wantsClients });

  const orgById = useMemo(() => new Map(organizations.map((o) => [o.id, o])), [organizations]);
  const personById = useMemo(() => new Map(individuals.map((p) => [p.id, p])), [individuals]);
  // The view says which kinds this directory is showing at all; the two
  // switches below say which of them the map is drawing right now, so a
  // care manager can look at places and people one at a time.
  const viewShowsPeople = view !== 'organizations';
  const showsPeople = viewShowsPeople && layers.people;
  const viewShowsOrganizations = view !== 'people';
  const showsOrganizations = viewShowsOrganizations && layers.organizations;

  // Only the rows the lists are showing: the map joins the directory's
  // filtered rows to the placement read by id.
  const visiblePins = useMemo<ProviderMapPinWire[]>(
    () => (providerMap.data?.pins ?? []).filter((p) => orgById.has(p.id)),
    [providerMap.data, orgById],
  );
  const visibleNotPlaced = useMemo<ProviderMapNotPlacedWire[]>(
    () => (providerMap.data?.not_placed ?? []).filter((n) => orgById.has(n.id)),
    [providerMap.data, orgById],
  );
  const visiblePersonPins = useMemo<IndividualMapPinWire[]>(
    () => (providerMap.data?.individual_pins ?? []).filter((p) => personById.has(p.id)),
    [providerMap.data, personById],
  );
  const visiblePeopleNotPlaced = useMemo<IndividualMapNotPlacedWire[]>(
    () => (providerMap.data?.individuals_not_placed ?? []).filter((n) => personById.has(n.id)),
    [providerMap.data, personById],
  );
  const notPlacedGroups = NOT_PLACED_GROUP_ORDER.map((reason) => ({
    reason,
    items: visibleNotPlaced
      .filter((n) => n.reason === reason)
      .map((n) => ({ id: n.id, label: n.name, href: `/t/${tenantSlug}/organizations/${n.id}` })),
  })).filter((g) => g.items.length > 0);
  const peopleNotPlacedGroups = NOT_PLACED_GROUP_ORDER.map((reason) => ({
    reason,
    items: visiblePeopleNotPlaced
      .filter((n) => n.reason === reason)
      .map((n) => ({ id: n.id, label: n.full_name, href: `/t/${tenantSlug}/individuals/${n.id}` })),
  })).filter((g) => g.items.length > 0);

  const nearClient = useMemo<NearClientState>(() => {
    // No client layer, no chip: a seat that cannot show clients never
    // reads one, so the chip would otherwise wait forever.
    if (nearClientId === null || !canShowClients) return { kind: 'none' };
    if (!clientMap.data) return { kind: 'loading', id: nearClientId };
    const pin = clientMap.data.pins.find((p) => p.client_id === nearClientId);
    if (pin) {
      return {
        kind: 'placed',
        id: pin.client_id,
        name: pin.display_name,
        point: { lat: pin.lat, lng: pin.lng },
        approximate: pin.precision === 'zip',
      };
    }
    const unplaced = clientMap.data.not_placed.find((c) => c.client_id === nearClientId);
    if (unplaced) return { kind: 'not_placed', id: unplaced.client_id, name: unplaced.display_name, reason: unplaced.reason };
    return { kind: 'unknown', id: nearClientId };
  }, [nearClientId, canShowClients, clientMap.data]);
  const nearPoint = nearClient.kind === 'placed' ? nearClient.point : null;
  const nearOutsideWorkingArea = nearPoint !== null && workingArea !== null && !insideBounds(nearPoint, workingArea);

  // The person's asks to move the map. The client the URL named when the
  // view mounted asks once, when the read first resolves them; after that
  // only a search choice asks (a clear, a refetch or the URL change a
  // choice causes never does).
  const [focusRequest, askFocus] = useFocusRequest();
  const mountNearIdRef = useRef(nearClientId);
  const urlAskSettledRef = useRef(nearClientId === null);
  useEffect(() => {
    if (urlAskSettledRef.current) return;
    if (nearClientId !== mountNearIdRef.current) {
      urlAskSettledRef.current = true;
      return;
    }
    if (nearClient.kind === 'loading' || nearClient.kind === 'none') return;
    urlAskSettledRef.current = true;
    if (nearClient.kind === 'placed') askFocus(nearClient.point);
  }, [nearClient, nearClientId, askFocus]);

  const chooseNearClient = (clientId: string): void => {
    onNearClientChange(clientId);
    const pin = clientMap.data?.pins.find((p) => p.client_id === clientId);
    if (pin) askFocus({ lat: pin.lat, lng: pin.lng });
  };

  const nearChip: MapClientChipState | null = ((): MapClientChipState | null => {
    switch (nearClient.kind) {
      case 'none':
        return null;
      case 'placed':
        return { kind: 'placed', name: nearClient.name, approximate: nearClient.approximate };
      case 'not_placed':
        return { kind: 'not_placed', name: nearClient.name, reasonLabel: clientNotPlacedLabel(nearClient.reason, workingStates) };
      case 'unknown':
        return { kind: 'unknown' };
      case 'loading':
        return { kind: 'loading' };
    }
  })();

  const activeCard = pinned ?? hovered;
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Where the pointer last touched a pin, and where it rested when a card
  // last closed (see the header: a resting pointer's re-hover is ignored).
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const restPointRef = useRef<{ x: number; y: number } | null>(null);
  const closeCard = (): void => {
    setPinned(null);
    setHovered(null);
    restPointRef.current = lastPointRef.current;
  };
  const hoverAnchor = (anchor: CardAnchor | null): void => {
    if (anchor === null) {
      setHovered(null);
      return;
    }
    const rest = restPointRef.current;
    const { point } = anchor;
    if (rest && Math.abs(point.x - rest.x) <= RESTING_POINTER_PX && Math.abs(point.y - rest.y) <= RESTING_POINTER_PX) return;
    restPointRef.current = null;
    lastPointRef.current = point;
    setHovered(anchor);
  };
  const hoverPin = (id: string | null, point: { x: number; y: number } | null): void => {
    hoverAnchor(id === null || point === null ? null : anchorFromMarker(id, point));
  };
  const tapAnchor = (next: CardAnchor): void => {
    lastPointRef.current = next.point;
    restPointRef.current = null;
    if (pinned?.kind === next.kind && pinned.id === next.id) closeCard();
    else setPinned(next);
  };
  // Escape closes the card, as it closes a dialog. Listening from mount (not
  // from the card's own mount) leaves no moment after a card opens when
  // Escape would be missed.
  const closeCardRef = useRef(closeCard);
  closeCardRef.current = closeCard;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeCardRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  // A tap or click on the map outside the card closes it. A pin is left to
  // its own handler (it opens its card, or closes its own when tapped again).
  const closeOnOutsidePress = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (activeCard === null) return;
    const target = e.target as Element | null;
    if (target && cardRef.current?.contains(target)) return;
    if (target?.closest?.('.map-marker-pin, .map-name-tag')) return;
    closeCard();
  };

  const providerPins = useMemo<MapPin[]>(() => {
    const orgPins: MapPin[] = visiblePins.map((p) => {
      const selected = pinned?.kind === 'organization' && pinned.id === p.id;
      return {
        id: markerIdFor('organization', p.id),
        lat: p.lat,
        lng: p.lng,
        color: colors.providers,
        title: `${p.name}, ${p.category_label}`,
        href: null,
        radius: selected ? PROVIDER_PIN_RADIUS + HOVER_RADIUS_DELTA : PROVIDER_PIN_RADIUS,
        weight: selected ? HOVER_WEIGHT : undefined,
      };
    });
    const personPins: MapPin[] = visiblePersonPins.map((p) => {
      const selected = pinned?.kind === 'person' && pinned.id === p.id;
      return {
        id: markerIdFor('person', p.id),
        lat: p.lat,
        lng: p.lng,
        color: colors.people,
        title: p.title ? `${p.full_name}, ${p.title}` : p.full_name,
        href: null,
        radius: selected ? PROVIDER_PIN_RADIUS + HOVER_RADIUS_DELTA : PROVIDER_PIN_RADIUS,
        weight: selected ? HOVER_WEIGHT : undefined,
      };
    });
    return [...(showsOrganizations ? orgPins : []), ...(showsPeople ? personPins : [])];
  }, [visiblePins, visiblePersonPins, colors.providers, colors.people, pinned, showsOrganizations, showsPeople]);

  const clientPins = useMemo<MapPin[]>(
    () =>
      // "My clients" draws the viewer's own clients only (the server's
      // caseload rule, `mine`); search below still finds any client she
      // may open.
      (clientMap.data?.pins ?? []).filter((p) => p.mine === true).map((p) => ({
        id: p.client_id,
        lat: p.lat,
        lng: p.lng,
        color: colors.clients,
        title: `${p.display_name}${p.precision === 'zip' ? APPROXIMATE_SUFFIX : ''}`,
        href: `/t/${tenantSlug}/clients/${p.client_id}`,
        radius: CLIENT_PIN_RADIUS,
        // Fully opaque here: the viewer's own clients are the point of this
        // layer, not a backdrop (the capacity map keeps its see-through client pins).
        opacity: 1,
      })),
    [clientMap.data, colors.clients, tenantSlug],
  );

  // LP-43: the Near pin carries the client's name tag and opens the record.
  const placementPin =
    nearPoint && nearClient.kind === 'placed'
      ? {
          lat: nearPoint.lat,
          lng: nearPoint.lng,
          color: colors.placement_pin,
          client: { name: capitalizeFirst(nearClient.name), href: `/t/${tenantSlug}/clients/${nearClient.id}` },
        }
      : null;

  const cardOrg = activeCard?.kind === 'organization' ? orgById.get(activeCard.id) : undefined;
  const cardPin = activeCard?.kind === 'organization' ? visiblePins.find((p) => p.id === activeCard.id) : undefined;
  const cardPerson = activeCard?.kind === 'person' ? personById.get(activeCard.id) : undefined;
  const cardPersonPin = activeCard?.kind === 'person' ? visiblePersonPins.find((p) => p.id === activeCard.id) : undefined;
  const nearName = nearClient.kind === 'placed' ? capitalizeFirst(nearClient.name) : null;
  const distanceTo = (point: LatLng): number | null => (nearPoint ? haversineMiles(nearPoint, point) : null);

  const emptyText = ((): string | null => {
    if (!providerMap.data) return null;
    if (view === 'organizations') return organizations.length === 0 ? 'Clear a filter to widen the map.' : null;
    if (view === 'people') return individuals.length === 0 ? 'Clear a filter to widen the map.' : null;
    return organizations.length === 0 && individuals.length === 0 ? 'Clear a filter to widen the map.' : null;
  })();

  // The map fills the window under the bar: the window, less the header
  // the bar sticks under, the bar, a gap above and below, and on a phone
  // the bottom tab bar.
  const barRef = useRef<HTMLDivElement | null>(null);
  const barHeight = useMeasuredHeight(barRef, BAR_PX_GUESS);
  const headerPx = isMobile ? HEADER_PX_PHONE : HEADER_PX;
  const reservedPx = headerPx + barHeight + 2 * MAP_GAP_PX + (isMobile ? TAB_BAR_PX_PHONE : 0);
  const mapHeightStyle = { '--directory-map-h': `max(${MAP_MIN_HEIGHT}, calc(100dvh - ${Math.round(reservedPx)}px))` } as CSSProperties;
  const mapHeightClass = 'h-[var(--directory-map-h)]';

  // The layer pills: the colour is the legend, the count what the layer draws.
  const layerPills = (
    <>
      {canShowClients && (
        <FilterPill
          label="My clients"
          icon={<Swatch color={colors.clients} testId="directory-map-legend-clients" />}
          count={clientMap.data ? clientPins.length : 0}
          active={showClients}
          onClick={() => toggleLayer('clients')}
          data-testid="directory-map-toggle-clients"
        />
      )}
      {viewShowsOrganizations && (
        <FilterPill
          label="Organizations"
          icon={<Swatch color={colors.providers} testId="directory-map-legend-providers" />}
          count={providerMap.data ? visiblePins.length : 0}
          active={layers.organizations}
          onClick={() => toggleLayer('organizations')}
          data-testid="directory-map-toggle-organizations"
        />
      )}
      {viewShowsPeople && (
        <FilterPill
          label="People"
          icon={<Swatch color={colors.people} testId="directory-map-legend-people" />}
          count={providerMap.data ? visiblePersonPins.length : 0}
          active={layers.people}
          onClick={() => toggleLayer('people')}
          data-testid="directory-map-toggle-people"
        />
      )}
    </>
  );
  const clientSearch = (fullWidth: boolean): ReactNode => (
    <MapClientSearch
      pins={clientMap.data?.pins ?? NO_PINS}
      notPlaced={clientMap.data?.not_placed ?? NO_NOT_PLACED}
      workingStates={workingStates}
      loading={clientMap.isLoading}
      error={clientMap.isError}
      filtersActive={false}
      onPick={chooseNearClient}
      onFocus={() => setSearchEngaged(true)}
      fullWidth={fullWidth}
    />
  );

  // What the map has to say sits on its corner, never above it.
  // Not while the seat is still loading: nothing has been turned off yet.
  const directoryLayersOff = whoami !== null && !showsOrganizations && !showsPeople;

  return (
    <section data-testid="directory-map" aria-label="Map">
      <div ref={barRef} data-testid="directory-map-bar" className="sticky z-40 bg-cream" style={{ top: headerPx }}>
        {isMobile ? (
          // Below 760px the search and the layer pills take a line each: the
          // search the whole width, the pills under it at their own size,
          // scrolling sideways only when they must.
          <>
            {canShowClients && (
              <div
                data-testid="directory-map-bar-first-line"
                className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} pt-3 pb-1`}
              >
                <div data-testid="directory-map-search-slot">{clientSearch(true)}</div>
              </div>
            )}
            <div
              data-testid="directory-map-layers"
              className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} flex items-center gap-2 overflow-x-auto scrollbar-none pb-3 ${canShowClients ? 'pt-1' : 'pt-3'}`}
            >
              {layerPills}
            </div>
          </>
        ) : (
          <div
            data-testid="directory-map-bar-first-line"
            className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} flex flex-nowrap items-center gap-2 py-3`}
          >
            {canShowClients && (
              <div className="shrink-0" data-testid="directory-map-search-slot">
                {clientSearch(false)}
              </div>
            )}
            <div
              data-testid="directory-map-layers"
              className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto scrollbar-none"
            >
              {layerPills}
            </div>
          </div>
        )}
        {filterRow}
      </div>

      <div className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} pt-4 pb-24`}>
        {/* Its own stacking context: the notes and pin cards on the map
            stay under the sticky bar and the client search's results. */}
        <div
          className={`relative isolate min-w-0 ${isMobile ? PHONE_MAP_INSET : 'w-full'}`}
          data-testid="directory-map-frame"
          style={mapHeightStyle}
          onPointerDownCapture={closeOnOutsidePress}
        >
          {workingArea === null ? (
            <div
              data-testid="directory-map-loading"
              className={`${mapHeightClass} flex w-full items-center justify-center rounded-card bg-sand text-sm text-ink-soft`}
            >
              Loading the map.
            </div>
          ) : (
          <LeafletMap
            tileUrl={tileUrlTemplate(tenantSlug)}
            cmPins={providerPins}
            clientPins={clientPins}
            lines={[]}
            placementPin={placementPin}
            focusRequest={focusRequest}
            showCms
            showClients={canShowClients && showClients}
            onOpen={(href) => navigate(href)}
            heightClass={mapHeightClass}
            canvasTestId="directory-map-canvas"
            homeBounds={workingArea}
            workingArea={workingArea}
            onCmPinHover={hoverPin}
            onCmPinTap={(id, point) => tapAnchor(anchorFromMarker(id, point))}
            onPlacementPinHover={(point) =>
              hoverAnchor(point === null || nearClient.kind !== 'placed' ? null : { kind: 'client', id: nearClient.id, point })
            }
            onPlacementPinTap={(point) => {
              if (nearClient.kind === 'placed') tapAnchor({ kind: 'client', id: nearClient.id, point });
            }}
          />
          )}

          <div
            data-testid="directory-map-notes"
            className="pointer-events-none absolute right-3 top-3 z-[60] flex max-w-[calc(100%-4.5rem)] flex-col items-end gap-1.5"
          >
            {nearChip && (
              <div className="pointer-events-auto max-w-full rounded-card shadow-sh1">
                <MapClientChip
                  state={nearChip}
                  placedPrefix="Near"
                  href={nearClientId === null ? undefined : `/t/${tenantSlug}/clients/${nearClientId}`}
                  testId="directory-map-near-client-chip"
                  clearTestId="directory-map-near-client-clear"
                  onClear={() => onNearClientChange(null)}
                  size="large"
                  icon={nearChip.kind === 'placed' ? <Swatch color={colors.placement_pin} testId="directory-map-legend-near" large /> : undefined}
                />
              </div>
            )}
            {nearOutsideWorkingArea && <MapNote testId="placement-outside-area">{PLACEMENT_OUTSIDE_AREA_TEXT}</MapNote>}
            {providerMap.isError && (
              <MapNote testId="directory-map-error" alert>
                The map didn't load. Reload the page, or switch to the list.
              </MapNote>
            )}
            {directoryLayersOff && <MapNote testId="directory-map-layers-off">{DIRECTORY_LAYERS_OFF_TEXT}</MapNote>}
            {emptyText && <MapNote testId="directory-map-empty">{emptyText}</MapNote>}
            {canShowClients && showClients && clientMap.isLoading && <MapNote>Loading client pins.</MapNote>}
            {canShowClients && showClients && clientMap.data && clientPins.length === 0 && (
              <MapNote testId="directory-map-no-own-clients">Once you are assigned a client, you'll see them on the map.</MapNote>
            )}
            {canShowClients && showClients && clientMap.isError && <MapNote alert>The client pins could not be loaded.</MapNote>}
          </div>

          {activeCard && cardOrg && cardPin && (
            <div
              ref={cardRef}
              className="absolute z-[70]"
              style={{ left: activeCard.point.x + 8, top: Math.max(0, activeCard.point.y - 8) }}
            >
              <ProviderPinCard
                tenantSlug={tenantSlug}
                org={cardOrg}
                pin={cardPin}
                distanceMiles={distanceTo({ lat: cardPin.lat, lng: cardPin.lng })}
                nearName={nearName}
                interactive={pinned !== null}
                onClose={closeCard}
              />
            </div>
          )}

          {activeCard && cardPerson && cardPersonPin && (
            <div
              ref={cardRef}
              className="absolute z-[70]"
              style={{ left: activeCard.point.x + 8, top: Math.max(0, activeCard.point.y - 8) }}
            >
              <PersonPinCard
                tenantSlug={tenantSlug}
                person={cardPerson}
                pin={cardPersonPin}
                distanceMiles={distanceTo({ lat: cardPersonPin.lat, lng: cardPersonPin.lng })}
                nearName={nearName}
                interactive={pinned !== null}
                onClose={closeCard}
              />
            </div>
          )}

          {activeCard?.kind === 'client' && nearClient.kind === 'placed' && activeCard.id === nearClient.id && (
            <div
              ref={cardRef}
              className="absolute z-[70]"
              style={{ left: activeCard.point.x + 8, top: Math.max(0, activeCard.point.y - 8) }}
            >
              <ClientPinCard
                tenantSlug={tenantSlug}
                clientId={nearClient.id}
                name={capitalizeFirst(nearClient.name)}
                approximate={nearClient.approximate}
                interactive={pinned !== null}
                onClose={closeCard}
              />
            </div>
          )}
        </div>

        {seesGaps && providerMap.data && (visibleNotPlaced.length > 0 || visiblePeopleNotPlaced.length > 0) && (
          <div className="mt-3 flex flex-col gap-2 text-sm text-ink-soft">
            {visibleNotPlaced.length > 0 && (
              <div data-testid="directory-map-not-placed">
                <p>
                  <span className="font-semibold text-ink">Not placed: {visibleNotPlaced.length}</span>
                </p>
                {notPlacedGroups.map((g) => (
                  <NotPlacedGroup
                    key={g.reason}
                    label={notPlacedGroupLabel(g.reason, workingStates)}
                    items={g.items}
                    testId={`directory-map-not-placed-${g.reason.replace(/_/g, '-')}`}
                    itemTestId="directory-map-not-placed-org"
                  />
                ))}
              </div>
            )}
            {visiblePeopleNotPlaced.length > 0 && (
              <div data-testid="directory-map-not-placed-people">
                <p>
                  <span className="font-semibold text-ink">People not placed: {visiblePeopleNotPlaced.length}</span>
                </p>
                {peopleNotPlacedGroups.map((g) => (
                  <NotPlacedGroup
                    key={g.reason}
                    label={notPlacedGroupLabel(g.reason, workingStates)}
                    items={g.items}
                    testId={`directory-map-not-placed-people-${g.reason.replace(/_/g, '-')}`}
                    itemTestId="directory-map-not-placed-person"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/** One quiet line on the map's corner. */
function MapNote({ testId, alert = false, children }: { testId?: string; alert?: boolean; children: ReactNode }): ReactNode {
  return (
    <p
      role={alert ? 'alert' : undefined}
      data-testid={testId}
      className={`max-w-lg rounded-card bg-cream/95 px-2.5 py-1 text-xs shadow-sh1 ${alert ? 'text-danger' : 'text-ink-soft'}`}
    >
      {children}
    </p>
  );
}

function Swatch({ color, hollow = false, testId, large = false }: { color: string; hollow?: boolean; testId?: string; large?: boolean }): ReactNode {
  return (
    <span
      aria-hidden="true"
      data-testid={testId}
      className={`inline-block shrink-0 rounded-full ${large ? 'h-4 w-4' : 'h-2.5 w-2.5'}`}
      style={hollow ? { border: `2px solid ${color}` } : { backgroundColor: color, border: '1.5px solid #FFFFFF' }}
    />
  );
}

/** The card palette's one accent not already a design-system token (the
 *  capacity map's CmCard rule colour, the printed brand's gold). */
const GOLD_RULE_COLOR = '#B8893D';

// LP-43: the cards read at one and a half times
// their first size (the name at text-2xl, every other line at text-lg).
const CARD_LINK_CLASS =
  'inline-block text-lg font-semibold text-sage-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';
const CARD_NAME_CLASS = 'flex flex-wrap items-center gap-2 text-2xl font-semibold leading-tight text-sage-800';
const CARD_LINE_CLASS = 'text-lg';

interface PinCardFrameProps {
  testId: string;
  dataAttr: Record<string, string>;
  /** A tapped card takes the pointer (its links are clickable); a hovered one does not. */
  interactive: boolean;
  onClose: () => void;
  heading: ReactNode;
  children: ReactNode;
}

/** The card's frame: the heading row with its close control, the gold rule, the body. */
function PinCardFrame({ testId, dataAttr, interactive, onClose, heading, children }: PinCardFrameProps): ReactNode {
  return (
    <div
      role={interactive ? 'dialog' : 'tooltip'}
      data-testid={testId}
      {...dataAttr}
      className={`w-fit max-w-sm rounded-[10px] border border-sage-200 bg-cream px-4 py-3 shadow-sh2 ${interactive ? '' : 'pointer-events-none'}`}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0">{heading}</div>
        {interactive && (
          <Button
            variant="ghost"
            size="sm"
            icon={<X size={22} aria-hidden="true" />}
            onClick={onClose}
            aria-label="Close"
            data-testid={`${testId}-close`}
            className="-mr-1.5 -mt-1 ml-auto shrink-0 !px-1.5"
          />
        )}
      </div>
      <div aria-hidden="true" className="my-2 h-[2px] w-12 rounded-pill" style={{ backgroundColor: GOLD_RULE_COLOR }} />
      {children}
    </div>
  );
}

function DistanceLine({ miles, nearName, testId }: { miles: number | null; nearName: string | null; testId: string }): ReactNode {
  if (miles === null) return null;
  return (
    <p className={`${CARD_LINE_CLASS} font-semibold text-sage-800`} data-testid={testId}>
      {formatDistanceMiles(miles)}
      {nearName ? ` from ${nearName}` : ''}
    </p>
  );
}

function PrecisionLine({
  precision,
  townOnly = false,
  testId,
}: {
  precision: ProviderMapPinWire['precision'];
  townOnly?: boolean;
  testId: string;
}): ReactNode {
  if (precision === 'street') return null;
  // An entry with no street is placed at its town by design, not by a
  // miss worth checking.
  if (townOnly) {
    return (
      <p className={`${CARD_LINE_CLASS} text-ink-soft`} data-testid={testId}>
        Placed at the town, not the street.
      </p>
    );
  }
  return (
    <p className={`${CARD_LINE_CLASS} text-ink-soft`} data-testid={testId}>
      Approximate location. Check the address.
    </p>
  );
}

interface ProviderPinCardProps {
  tenantSlug: string;
  org: ProviderForSearch;
  pin: ProviderMapPinWire;
  /** Straight-line miles from the picked client; null when none is picked. */
  distanceMiles: number | null;
  nearName: string | null;
  interactive: boolean;
  onClose: () => void;
}

function ProviderPinCard({ tenantSlug, org, pin, distanceMiles, nearName, interactive, onClose }: ProviderPinCardProps): ReactNode {
  const categoryLabel = pin.category_label || categoryDisplayFromSlug(org.category_slug) || org.category_slug;
  return (
    <PinCardFrame
      testId="directory-map-pin-card"
      dataAttr={{ 'data-org-id': org.id }}
      interactive={interactive}
      onClose={onClose}
      heading={
        <>
          <p className={CARD_NAME_CLASS} data-testid="directory-map-pin-card-name">
            <span>{org.name}</span>
            <RecommendedBadge
              isRecommended={org.is_recommended}
              recommendedAt={org.recommended_at}
              recommendedByName={org.recommended_by_display_name}
            />
          </p>
          <p className={`${CARD_LINE_CLASS} text-ink-soft`} data-testid="directory-map-pin-card-category">
            {categoryLabel}
          </p>
        </>
      }
    >
      {org.phone && (
        <p className={`${CARD_LINE_CLASS} text-ink`} data-testid="directory-map-pin-card-phone">
          {formatPhoneDisplay(org.phone)}
        </p>
      )}
      <DistanceLine miles={distanceMiles} nearName={nearName} testId="directory-map-pin-card-distance" />
      <PrecisionLine precision={pin.precision} townOnly={pin.town_only === true} testId="directory-map-pin-card-precision" />
      {pin.people.length > 0 && (
        <div className="mt-1.5" data-testid="directory-map-pin-card-people">
          <p className="text-base font-semibold uppercase tracking-wider text-sage-700">
            People we know here ({pin.people.length})
          </p>
          <ul className="mt-1 flex max-h-60 flex-col gap-1.5 overflow-y-auto">
            {pin.people.map((person) => (
              <li key={person.id} className={`${CARD_LINE_CLASS} text-ink`} data-testid="directory-map-pin-card-person">
                <span className="flex flex-wrap items-center gap-1.5">
                  <Link to={`/t/${tenantSlug}/individuals/${person.id}`} className="font-semibold text-sage-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
                    {person.full_name}
                  </Link>
                  <RecommendedBadge isRecommended={person.is_recommended} recommendedAt={null} recommendedByName={null} />
                </span>
                {(person.title || person.role) && (
                  <span className="block text-ink-soft">{[person.title, person.role].filter(Boolean).join(' · ')}</span>
                )}
                {person.phone && <span className="block">{formatPhoneDisplay(person.phone)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Link to={`/t/${tenantSlug}/organizations/${org.id}`} className={`mt-1 ${CARD_LINK_CLASS}`} data-testid="directory-map-pin-card-open">
        Open the organization
      </Link>
    </PinCardFrame>
  );
}

interface PersonPinCardProps {
  tenantSlug: string;
  person: IndividualForSearch;
  pin: IndividualMapPinWire;
  distanceMiles: number | null;
  nearName: string | null;
  interactive: boolean;
  onClose: () => void;
}

function PersonPinCard({ tenantSlug, person, pin, distanceMiles, nearName, interactive, onClose }: PersonPinCardProps): ReactNode {
  return (
    <PinCardFrame
      testId="directory-map-person-card"
      dataAttr={{ 'data-person-id': person.id }}
      interactive={interactive}
      onClose={onClose}
      heading={
        <>
          <p className={CARD_NAME_CLASS} data-testid="directory-map-person-card-name">
            <span>{person.full_name}</span>
            <RecommendedBadge
              isRecommended={person.is_recommended}
              recommendedAt={person.recommended_at}
              recommendedByName={person.recommended_by_display_name}
            />
          </p>
          {pin.title && (
            <p className={`${CARD_LINE_CLASS} text-ink-soft`} data-testid="directory-map-person-card-title">
              {pin.title}
            </p>
          )}
        </>
      }
    >
      {pin.specialties && (
        <p className={`${CARD_LINE_CLASS} text-ink-soft`} data-testid="directory-map-person-card-specialties">
          {pin.specialties}
        </p>
      )}
      {pin.phone && (
        <p className={`${CARD_LINE_CLASS} text-ink`} data-testid="directory-map-person-card-phone">
          {formatPhoneDisplay(pin.phone)}
        </p>
      )}
      <DistanceLine miles={distanceMiles} nearName={nearName} testId="directory-map-person-card-distance" />
      <PrecisionLine precision={pin.precision} townOnly={pin.town_only === true} testId="directory-map-person-card-precision" />
      <Link to={`/t/${tenantSlug}/individuals/${person.id}`} className={`mt-1 ${CARD_LINK_CLASS}`} data-testid="directory-map-person-card-open">
        Open the person
      </Link>
    </PinCardFrame>
  );
}

interface ClientPinCardProps {
  tenantSlug: string;
  clientId: string;
  name: string;
  approximate: boolean;
  interactive: boolean;
  onClose: () => void;
}

/** LP-43: the Near client's pin opens a card as the
 *  other pins do, with the way into her record, rather than leaving the
 *  page on a click. */
function ClientPinCard({ tenantSlug, clientId, name, approximate, interactive, onClose }: ClientPinCardProps): ReactNode {
  return (
    <PinCardFrame
      testId="directory-map-client-card"
      dataAttr={{ 'data-client-id': clientId }}
      interactive={interactive}
      onClose={onClose}
      heading={
        <>
          <p className={CARD_NAME_CLASS} data-testid="directory-map-client-card-name">
            <span>{name}</span>
          </p>
          <p className={`${CARD_LINE_CLASS} text-ink-soft`}>Client</p>
        </>
      }
    >
      {approximate && (
        <p className={`${CARD_LINE_CLASS} text-ink-soft`} data-testid="directory-map-client-card-precision">
          {APPROXIMATE_LOCATION_TEXT}
        </p>
      )}
      <Link to={`/t/${tenantSlug}/clients/${clientId}`} className={`mt-1 ${CARD_LINK_CLASS}`} data-testid="directory-map-client-card-open">
        Open the client record
      </Link>
    </PinCardFrame>
  );
}
